// App.tsx (полная версия с отдельной страницей администратора)
import React, { Suspense, lazy } from 'react';
import ScrollToTop from './components/ScrollToTop';
import PlacementModalHost from './components/PlacementModalHost';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ConfirmProvider } from './contexts/ConfirmContext';
import { ToastProvider } from './contexts/ToastContext';
import PaymentRequiredModal from './components/PaymentRequiredModal';
import PermissionRoute from './components/PermissionRoute';
import MindMapEditor from './pages/MindMapEditor';
import Email from './pages/HRDigitalHub/Email';
import PaymentPage from './pages/PaymentPage';
import PaymentSuccessPage from './pages/PaymentSuccessPage';
import NotFoundPage from './pages/NotFoundPage';
import FixedTestRequestBar from './components/FixedTestRequestBar';

// Статические импорты
import LandingPage from './pages/LandingPage';
import AboutPage from './pages/AboutPage';
import PricingPage from './pages/PricingPage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import VerifyEmailPage from './pages/VerifyEmailPage';
import Dashboard from './pages/Dashboard';
import BIDashboardList from './pages/BIDashboardList';
import BIDashboardBuilder from './pages/BIDashboardBuilder';
import BIViewer from './pages/BIViewer';
import InterviewScriptsList from './pages/HRDigitalHub/components/InterviewScriptsList';
import InterviewScriptEditor from './pages/HRDigitalHub/components/InterviewScriptEditor';
import InterviewScriptRunner from './pages/HRDigitalHub/components/InterviewScriptRunner';
import ELearningDashboard from './pages/ELearning/ELearningDashboard';
import ELearningLayout from './pages/ELearning/ELearningLayout';
import TestsList from './pages/ELearning/TestsList';
import BenefitsList from './pages/ELearning/BenefitsList';
import TestConstructor from './pages/ELearning/TestConstructor';
import UserProfile from './pages/ELearning/UserProfile';
import TestRunner from './pages/ELearning/TestRunner';
import TrashTests from './pages/ELearning/TrashTests';
import FavoritesTests from './pages/ELearning/FavoritesTests';
import RatingPage from './pages/ELearning/RatingPage';
import AnalyticsPage from './pages/ELearning/AnalyticsPage';
import BenefitConstructor from './pages/ELearning/BenefitConstructor';
import MyAssignmentsPage from './pages/ELearning/MyAssignmentsPage';
import VerifyCertificatePage from './pages/ELearning/VerifyCertificatePage';
import AuditLogPage from './pages/ELearning/AuditLogPage';
import HRDashboardPage from './pages/ELearning/HRDashboardPage';
import AchievementsPage from './pages/ELearning/AchievementsPage';
import CalendarPage from './pages/ELearning/CalendarPage';
import ComparativeAnalyticsPage from './pages/ELearning/ComparativeAnalyticsPage';
import KPIPage from './pages/ELearning/KPIPage';
import CommentsPage from './pages/ELearning/CommentsPage';
import CertificatePrintPage from './pages/ELearning/CertificatePrintPage';
import LearningPathsPage from './pages/ELearning/LearningPathsPage';
import LearningPathDetailPage from './pages/ELearning/LearningPathDetailPage';
import LearningPathConstructor from './pages/ELearning/LearningPathConstructor';
import ELearningAutoRulesPage from './pages/ELearning/ELearningAutoRulesPage';
import TestResultPage from './pages/ELearning/TestResultPage';
import TaskBenefitsList from './pages/ELearning/TaskBenefitsList';
import TaskBenefitConstructor from './pages/ELearning/TaskBenefitConstructor';
import TaskBenefitDetail from './pages/ELearning/TaskBenefitDetail';
import RecruitmentLayout from './pages/RecruitmentAnalytics/RecruitmentLayout';
import RecruitmentDashboard from './pages/RecruitmentAnalytics/Dashboard';
import Vacancies from './pages/RecruitmentAnalytics/Vacancies';
import Candidates from './pages/RecruitmentAnalytics/Candidates';
import Requisitions from './pages/RecruitmentAnalytics/Requisitions';
import { default as RecruitmentVacancyDetail } from './pages/RecruitmentAnalytics/components/VacancyDetail';
import { default as RecruitmentCandidateDetail } from './pages/RecruitmentAnalytics/components/CandidateDetail';
import { default as RecruitmentRequisitionDetail } from './pages/RecruitmentAnalytics/components/RequisitionDetail';
import CandidateComparison from './pages/RecruitmentAnalytics/components/CandidateComparison';
import PredictiveDashboard from './pages/RecruitmentAnalytics/PredictiveDashboard';
import RecruiterPerformanceDashboard from './pages/RecruitmentAnalytics/RecruiterPerformanceDashboard';
import RecruiterDetail from './pages/RecruitmentAnalytics/RecruiterDetail';
import LaborMarketDashboard from './pages/RecruitmentAnalytics/LaborMarketDashboard';
import AlertsPage from './pages/RecruitmentAnalytics/AlertsPage';
import VacancyAging from './pages/RecruitmentAnalytics/VacancyAging';
import RejectionAnalysis  from './pages/RecruitmentAnalytics/RejectionAnalysis';
import PlanVsActual from './pages/RecruitmentAnalytics/PlanVsActual';
import DailyDigest from './pages/RecruitmentAnalytics/DailyDigest';
import StageDurationChart from './pages/RecruitmentAnalytics/StageDurationChart';

import CompetencyInterview from './pages/InterviewAssistant/CompetencyInterview';
import ProjectiveInterview from './pages/InterviewAssistant/ProjectiveInterview';
import CaseInterview from './pages/InterviewAssistant/CaseInterview';
import ThreePlusMinusInterview from './pages/InterviewAssistant/ThreePlusMinusInterview';
import ProvocationInterview from './pages/InterviewAssistant/ProvocationInterview';
import InformationCredibility from './pages/InterviewAssistant/InformationCredibility';
import KPIProfiles from './pages/KPI/KPIProfiles';
import PublicTestRunner from './pages/ELearning/PublicTestRunner';

// Ленивая загрузка
import lazyWithRetry from './utils/lazyWithRetry';
import SalesDealsPage from './pages/SalesCRM/DealsPage';
const PortalLayout = lazyWithRetry(() => import('./pages/Portal/PortalLayout'));
const PortalHome = lazyWithRetry(() => import('./pages/Portal/PortalHome'));
const PortalNews = lazyWithRetry(() => import('./pages/Portal/PortalNews'));
const PortalNewsNew = lazyWithRetry(() => import('./pages/Portal/PortalNewsNew'));
const PortalNewsDetail = lazyWithRetry(() => import('./pages/Portal/PortalNewsDetail'));
const PortalStub = lazyWithRetry(() => import('./pages/Portal/PortalStub'));
const PortalKnowledge = lazyWithRetry(() => import('./pages/Portal/PortalKnowledge'));
const PortalKnowledgeNew = lazyWithRetry(() => import('./pages/Portal/PortalKnowledgeNew'));
const PortalKnowledgeDetail = lazyWithRetry(() => import('./pages/Portal/PortalKnowledgeDetail'));
const PortalIdeas = lazyWithRetry(() => import('./pages/Portal/PortalIdeas'));
const PortalTeams = lazyWithRetry(() => import('./pages/Portal/PortalTeams'));
const PortalAchievements = lazyWithRetry(() => import('./pages/Portal/PortalAchievements'));
const PortalPulse = lazyWithRetry(() => import('./pages/Portal/PortalPulse'));
const PortalRecognitions = lazyWithRetry(() => import('./pages/Portal/PortalRecognitions'));
const PortalEmployees = lazyWithRetry(() => import('./pages/Portal/PortalEmployees'));
const PortalStructure = lazyWithRetry(() => import('./pages/Portal/PortalStructure'));

const ChartsPage = lazyWithRetry(() => import('./pages/ChartsPage'));
const HHAnalytics = lazyWithRetry(() => import('./pages/HHAnalytics'));
const HHModulePage = lazyWithRetry(() => import('./pages/HHModulePage'));
const PdfToWordConverter = lazyWithRetry(() => import('./pages/PdfToWordConverter'));
const CompatibilityChecker = lazyWithRetry(() => import('./pages/CompatibilityChecker'));

const TestSelector = lazyWithRetry(() => import('./pages/EmployeeTests/TestSelector'));
const EmployeeTestRunner = lazyWithRetry(() => import('./pages/EmployeeTests/TestRunner'));
const TestResults = lazyWithRetry(() => import('./pages/EmployeeTests/TestResults'));

const DiscTestSelector = lazyWithRetry(() => import('./pages/DiscTest/DiscTestSelector'));
const DiscTestRunner = lazyWithRetry(() => import('./pages/DiscTest/DiscTestRunner'));
const DiscTestResults = lazyWithRetry(() => import('./pages/DiscTest/DiscTestResults'));

const PsychTestSelector = lazyWithRetry(() => import('./pages/PsychTests/PsychTestSelector'));
const PsychTestRunner = lazyWithRetry(() => import('./pages/PsychTests/PsychTestRunner'));
const PsychTestResults = lazyWithRetry(() => import('./pages/PsychTests/PsychTestResults'));

const MotivatorsHandbook = lazyWithRetry(() => import('./pages/MotivatorsHandbook/MotivatorsHandbook'));

const HRMetrics = lazyWithRetry(() => import('./pages/HRMetrics/HRMetrics'));
const HRMetricsCalculator = lazyWithRetry(() => import('./pages/HRMetrics/HRMetricsCalculator'));

// HR-модуль
const DashboardHR = lazyWithRetry(() => import('./pages/HRDigitalHub/Dashboard'));
const EmployeeList = lazyWithRetry(() => import('./pages/HRDigitalHub/components/EmployeeList'));
const EmployeeCard = lazyWithRetry(() => import('./pages/HRDigitalHub/components/EmployeeCard'));
const RequisitionList = lazyWithRetry(() => import('./pages/HRDigitalHub/components/RequisitionList'));
const RequisitionForm = lazyWithRetry(() => import('./pages/HRDigitalHub/components/RequisitionForm'));
const VacancyList = lazyWithRetry(() => import('./pages/HRDigitalHub/components/VacancyList'));
const VacancyDetail = lazyWithRetry(() => import('./pages/HRDigitalHub/components/VacancyDetail'));
const CandidateKanban = lazyWithRetry(() => import('./pages/HRDigitalHub/components/CandidateKanban'));
const CandidateProfile = lazyWithRetry(() => import('./pages/HRDigitalHub/components/CandidateProfile'));
const ChecklistTemplatesPage = lazyWithRetry(() => import('./pages/HRDigitalHub/ChecklistTemplatesPage'));
const ChecklistAnalyticsPage = lazyWithRetry(() => import('./pages/HRDigitalHub/ChecklistAnalyticsPage'));
const MassFunnelPage = lazyWithRetry(() => import('./pages/HRDigitalHub/MassFunnelPage'));
const MassMailingsPage = lazyWithRetry(() => import('./pages/HRDigitalHub/MassMailingsPage'));
const OnboardingDashboard = lazyWithRetry(() => import('./pages/HRDigitalHub/OnboardingDashboard'));
const RecruitersKpiPage = lazyWithRetry(() => import('./pages/HRDigitalHub/RecruitersKpiPage'));
const SourceAnalyticsPage = lazyWithRetry(() => import('./pages/HRDigitalHub/SourceAnalyticsPage'));
const TalentPool = lazyWithRetry(() => import('./pages/HRDigitalHub/components/TalentPool'));
const TalentPoolForm = lazyWithRetry(() => import('./pages/HRDigitalHub/components/TalentPoolForm'));
const TalentPoolDetail = lazyWithRetry(() => import('./pages/HRDigitalHub/components/TalentPoolDetail'));
const VacationCalendar = lazyWithRetry(() => import('./pages/HRDigitalHub/components/VacationCalendar'));
const DocumentsManager = lazyWithRetry(() => import('./pages/HRDigitalHub/components/DocumentsManager'));
const HRDocumentsLayout = lazyWithRetry(() => import('./pages/HRDocuments/HRDocumentsLayout'));
const HRDocumentsPage = lazyWithRetry(() => import('./pages/HRDocuments/HRDocumentsPage'));
const UnifiedCalendar = lazyWithRetry(() => import('./pages/Calendar/UnifiedCalendar'));
const RoleAnalytics = lazyWithRetry(() => import('./pages/Analytics/RoleAnalytics'));
const BankPage = lazyWithRetry(() => import('./pages/AccountingCRM/BankPage'));
const AccountingDocumentsPage = lazyWithRetry(() => import('./pages/AccountingCRM/AccountingDocumentsPage'));
const AccountingTaxPage = lazyWithRetry(() => import('./pages/AccountingCRM/AccountingTaxPage'));
const KDPDashboard = lazyWithRetry(() => import('./pages/HRDocuments/KDPDashboard'));
const KDPOrdersPage = lazyWithRetry(() => import('./pages/HRDocuments/KDPOrdersPage'));
const KDPReportsPage = lazyWithRetry(() => import('./pages/HRDocuments/KDPReportsPage'));
const KDPCalendarPage = lazyWithRetry(() => import('./pages/HRDocuments/KDPCalendarPage'));
const HRCertificatesPage = lazyWithRetry(() => import('./pages/HRDocuments/HRCertificatesPage'));
const HRAcquaintancesPage = lazyWithRetry(() => import('./pages/HRDocuments/HRAcquaintancesPage'));
const HRRequestsPage = lazyWithRetry(() => import('./pages/HRDocuments/HRRequestsPage'));
const HRBulkSignPage = lazyWithRetry(() => import('./pages/HRDocuments/HRBulkSignPage'));
const MyHrDashboard = lazyWithRetry(() => import('./pages/HRDocuments/MyHrDashboard'));
const HRSalariesPage = lazyWithRetry(() => import('./pages/HRDocuments/SalariesPage'));
const KDPEmployeesPage = lazyWithRetry(() => import('./pages/HRDocuments/KDPEmployeesPage'));
const KDPVacationsPage = lazyWithRetry(() => import('./pages/HRDocuments/KDPVacationsPage'));
const KDPSchedulePage = lazyWithRetry(() => import('./pages/HRDocuments/KDPSchedulePage'));
const HRProcessesPage = lazyWithRetry(() => import('./pages/HRDocuments/HRProcessesPage'));
const HRLaborSafetyPage = lazyWithRetry(() => import('./pages/HRDocuments/HRLaborSafetyPage'));
const HREmploymentBooksPage = lazyWithRetry(() => import('./pages/HRDocuments/HREmploymentBooksPage'));
const KDPSettingsPage = lazyWithRetry(() => import('./pages/HRDocuments/KDPSettingsPage'));
const SalesLayout = lazyWithRetry(() => import('./pages/SalesCRM/SalesLayout'));
const SalesDashboard = lazyWithRetry(() => import('./pages/SalesCRM/SalesDashboard'));
const SalesTasksPage = lazyWithRetry(() => import('./pages/SalesCRM/TasksPage'));
const SalesApprovalsPage = lazyWithRetry(() => import('./pages/SalesCRM/ApprovalsPage'));
const SalesProcessesPage = lazyWithRetry(() => import('./pages/SalesCRM/ProcessesPage'));
const SalesBoardsPage = lazyWithRetry(() => import('./pages/SalesCRM/BoardsPage'));
const SalesClientsPage = lazyWithRetry(() => import('./pages/SalesCRM/ClientsPage'));
const SalesReportsPage = lazyWithRetry(() => import('./pages/SalesCRM/ReportsPage'));
const SalesSettingsPage = lazyWithRetry(() => import('./pages/SalesCRM/SettingsPage'));
const SalesAutomationPage = lazyWithRetry(() => import('./pages/SalesCRM/AutomationPage'));
const SalesLeadFormsPage = lazyWithRetry(() => import('./pages/SalesCRM/LeadFormsPage'));
const SalesEmailSettingsPage = lazyWithRetry(() => import('./pages/SalesCRM/EmailSettingsPage'));
const SalesEmailTemplatesPage = lazyWithRetry(() => import('./pages/SalesCRM/EmailTemplatesPage'));
const SalesEmailSequencesPage = lazyWithRetry(() => import('./pages/SalesCRM/EmailSequencesPage'));
const SalesGamificationPage = lazyWithRetry(() => import('./pages/SalesCRM/GamificationPage'));
const SalesGanttPage = lazyWithRetry(() => import('./pages/SalesCRM/GanttPage'));
const AccountingLayout = lazyWithRetry(() => import('./pages/AccountingCRM/AccountingLayout'));
const AccountingDashboard = lazyWithRetry(() => import('./pages/AccountingCRM/AccountingDashboard'));
const InvoicesPage = lazyWithRetry(() => import('./pages/AccountingCRM/InvoicesPage'));
const AccountingRequestsPage = lazyWithRetry(() => import('./pages/AccountingCRM/RequestsPage'));
const PaymentsPage = lazyWithRetry(() => import('./pages/AccountingCRM/PaymentsPage'));
const CounterpartiesPage = lazyWithRetry(() => import('./pages/AccountingCRM/CounterpartiesPage'));
const OperationsPage = lazyWithRetry(() => import('./pages/AccountingCRM/OperationsPage'));
const PaymentCalendarPage = lazyWithRetry(() => import('./pages/AccountingCRM/PaymentCalendarPage'));
const AccountsPage = lazyWithRetry(() => import('./pages/AccountingCRM/AccountsPage'));
const AccountingReportsPage = lazyWithRetry(() => import('./pages/AccountingCRM/AccountingReportsPage'));
const AccountingSettingsPage = lazyWithRetry(() => import('./pages/AccountingCRM/AccountingSettingsPage'));
const HRReports = lazyWithRetry(() => import('./pages/HRDigitalHub/components/HRReports'));
const EmployeeForm = lazyWithRetry(() => import('./pages/HRDigitalHub/components/EmployeeForm'));
const VacancyForm = lazyWithRetry(() => import('./pages/HRDigitalHub/components/VacancyForm'));
const CandidateList = lazyWithRetry(() => import('./pages/HRDigitalHub/components/CandidateList'));
const CandidateForm = lazyWithRetry(() => import('./pages/HRDigitalHub/components/CandidateForm'));
const CompanyList = lazyWithRetry(() => import('./pages/HRDigitalHub/components/CompanyList'));
const CompanyForm = lazyWithRetry(() => import('./pages/HRDigitalHub/components/CompanyForm'));
const CompanyDetail = lazyWithRetry(() => import('./pages/HRDigitalHub/components/CompanyDetail'));
const ManagePermissions = lazyWithRetry(() => import('./pages/HRDigitalHub/components/ManagePermissions'));
const KanbanDashboard = lazyWithRetry(() => import('./pages/HRDigitalHub/components/KanbanDashboard'));

const TemplatesPage = lazyWithRetry(() => import('./pages/TemplatesPage/TemplatesPage'));
const SOPPage = lazyWithRetry(() => import('./pages/SOPPage/SOPPage'));
const PsychodiagnosticsPage = lazyWithRetry(() => import('./pages/Psychodiagnostics/PsychodiagnosticsPage'));
const PsychodiagnosticsTestRunner = lazyWithRetry(() => import('./pages/Psychodiagnostics/TestRunner'));
const InterviewAssistant = lazyWithRetry(() => import('./pages/InterviewAssistant/InterviewAssistant'));
const ResetPasswordPage = lazyWithRetry(() => import('./pages/ResetPasswordPage'));

// Админ-панель
const AdminPage = lazyWithRetry(() => import('./pages/AdminPage'));

// Публичная страница теста (без авторизации)
const TestPublicRunner = lazyWithRetry(() => import('./pages/TestPublicRunner'));
const PublicAcquaintancePage = lazyWithRetry(() => import('./pages/Public/PublicAcquaintancePage'));
const PublicConsentPage = lazyWithRetry(() => import('./pages/Public/PublicConsentPage'));
const ShareVacancy = lazyWithRetry(() => import('./pages/ShareVacancy'));

// ErrorBoundary
class ErrorBoundary extends React.Component<
  { children: React.ReactNode; fallback?: React.ReactNode },
  { hasError: boolean; error?: Error }
> {
  constructor(props: any) {
    super(props);
    this.state = { hasError: false };
  }
  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }
  componentDidCatch(error: Error, errorInfo: any) {
    console.error('ErrorBoundary caught an error:', error, errorInfo);
  }
  render() {
    if (this.state.hasError) {
      return (
        this.props.fallback || (
          <div style={{ padding: 40, textAlign: 'center', color: '#1a1a2e' }}>
            <h2>Что-то пошло не так в этом разделе.</h2>
            <p>Пожалуйста, обновите страницу или вернитесь позже.</p>
            <button
              onClick={() => window.location.reload()}
              style={{
                padding: '10px 20px',
                borderRadius: 30,
                background: '#2575fc',
                color: 'white',
                border: 'none',
                cursor: 'pointer',
              }}
            >
              Обновить
            </button>

          </div>
        )
      );
    }
    return this.props.children;
  }
}

const LoadingFallback = () => <div style={{ padding: '20px', textAlign: 'center' }}>Загрузка...</div>;

const ProtectedRoute: React.FC<{ element: React.ReactElement }> = ({ element }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  if (!user) return <Navigate to="/login" replace />;

  // Страницы, доступные без оплаты: дашборд и оплата
  const freeAccessPaths = ['/dashboard', '/payment', '/payment/success'];
  if (freeAccessPaths.includes(location.pathname)) {
    return element;
  }

  const hasAccess = user.role === 'superadmin' || user.paid || (user.demoExpiresAt && new Date(user.demoExpiresAt) > new Date());
  if (!hasAccess) {
    return <PaymentRequiredModal isOpen={true} onClose={() => navigate('/dashboard')} />;
  }
  return element;
};

const AdminRoute: React.FC<{ element: React.ReactElement }> = ({ element }) => {
  const { user } = useAuth();
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== 'admin') return <Navigate to="/dashboard" replace />;
  return element;
};

const PublicRoute: React.FC<{ element: React.ReactElement }> = ({ element }) => {
  const { user } = useAuth();
  return user ? <Navigate to="/dashboard" replace /> : element;
};

function AppRoutes() {
  const location = useLocation();
  React.useEffect(() => {
    console.log('Current route:', location.pathname);
  }, [location]);

  return (
    <Suspense fallback={<LoadingFallback />}>
    <FixedTestRequestBar />
      <ScrollToTop />
      <Routes>
              <Route path="/test-public/:token" element={<TestPublicRunner />} />
              <Route path="/public/acquaintance/:token" element={<PublicAcquaintancePage />} />
              <Route path="/public/consent/:token" element={<PublicConsentPage />} />
                <Route path="/share/vacancy/:token" element={<ShareVacancy />} />
              {/* Публичные маршруты */}
        <Route path="/" element={<PublicRoute element={<LandingPage />} />} />
        <Route path="/about" element={<AboutPage />} />
        <Route path="/login" element={<PublicRoute element={<LoginPage />} />} />
        <Route path="/pricing" element={<PublicRoute element={<PricingPage />} />} />
        <Route path="/register" element={<PublicRoute element={<RegisterPage />} />} />
        <Route path="/verify-email" element={<PublicRoute element={<VerifyEmailPage />} />} />

        {/* Публичное прохождение теста (без авторизации) */}

        {/* Защищённые маршруты */}
        <Route path="/dashboard" element={<ProtectedRoute element={<Dashboard />} />} />
        {/* ═══ Корпоративный портал ═══ */}
        <Route path="/app/portal" element={<ProtectedRoute element={<PortalLayout />} />}>
          <Route index element={<PortalHome />} />
          <Route path="news" element={<PortalNews />} />
          <Route path="news/new" element={<PortalNewsNew />} />
          <Route path="news/:id" element={<PortalNewsDetail />} />
          <Route path="news/:id/edit" element={<PortalNewsNew />} />
          <Route path="pulse" element={<PortalPulse />} />
          <Route path="recognitions" element={<PortalRecognitions />} />
          <Route path="employees" element={<PortalEmployees />} />
          <Route path="structure" element={<PortalStructure />} />
          <Route path="knowledge" element={<PortalKnowledge />} />
          <Route path="knowledge/new" element={<PortalKnowledgeNew />} />
          <Route path="knowledge/:id" element={<PortalKnowledgeDetail />} />
          <Route path="knowledge/:id/edit" element={<PortalKnowledgeNew />} />
          <Route path="ideas" element={<PortalIdeas />} />
          <Route path="teams" element={<PortalTeams />} />
          <Route path="achievements" element={<PortalAchievements />} />
        </Route>

        <Route path="/payment" element={<ProtectedRoute element={<PaymentPage />} />} />
        <Route path="/payment/success" element={<ProtectedRoute element={<PaymentSuccessPage />} />} />

        <Route path="/app/bi" element={<ProtectedRoute element={<PermissionRoute permissionKey="canViewBi"><BIDashboardList /></PermissionRoute>} />} />
        <Route path="/app/bi/builder/new" element={<ProtectedRoute element={<PermissionRoute permissionKey="canViewBi"><BIDashboardBuilder /></PermissionRoute>} />} />
        <Route path="/app/bi/builder/:id" element={<ProtectedRoute element={<PermissionRoute permissionKey="canViewBi"><BIDashboardBuilder /></PermissionRoute>} />} />
        <Route path="/app/bi/viewer/:id" element={<ProtectedRoute element={<PermissionRoute permissionKey="canViewBi"><BIViewer /></PermissionRoute>} />} />
        <Route path="/app/bi/mindmaps/new" element={<ProtectedRoute element={<PermissionRoute permissionKey="canViewBi"><MindMapEditor /></PermissionRoute>} />} />
        <Route path="/app/bi/mindmaps/:id" element={<ProtectedRoute element={<PermissionRoute permissionKey="canViewBi"><MindMapEditor /></PermissionRoute>} />} />
        <Route path="/app/bi/:id/builder" element={<ProtectedRoute element={<PermissionRoute permissionKey="canViewBi"><BIDashboardBuilder /></PermissionRoute>} />} />

        <Route path="/app/charts" element={<ProtectedRoute element={<ChartsPage />} />} />
        <Route path="/app/hh-analytics" element={<ProtectedRoute element={<HHAnalytics />} />} />
        <Route path="/app/hh-search" element={<ProtectedRoute element={<HHModulePage />} />} />
        <Route path="/app/pdf2word" element={<ProtectedRoute element={<PdfToWordConverter />} />} />
        <Route path="/app/compatibility" element={<ProtectedRoute element={<CompatibilityChecker />} />} />

        <Route path="/app/tests" element={<ProtectedRoute element={<TestSelector />} />} />
        <Route path="/app/tests/:testId" element={<ProtectedRoute element={<EmployeeTestRunner />} />} />
        <Route path="/app/tests/:testId/results" element={<ProtectedRoute element={<TestResults />} />} />

        <Route path="/app/disc-test" element={<ProtectedRoute element={<DiscTestSelector />} />} />
        <Route path="/app/disc-test/:testId" element={<ProtectedRoute element={<DiscTestRunner />} />} />
        <Route path="/app/disc-test/:testId/results" element={<ProtectedRoute element={<DiscTestResults />} />} />

        <Route path="/app/psych-tests" element={<ProtectedRoute element={<PsychTestSelector />} />} />
        <Route path="/app/psych-tests/:testId" element={<ProtectedRoute element={<PsychTestRunner />} />} />
        <Route path="/app/psych-tests/:testId/results" element={<ProtectedRoute element={<PsychTestResults />} />} />

        <Route path="/app/motivators" element={<ProtectedRoute element={<MotivatorsHandbook />} />} />

        <Route path="/app/hr-metrics" element={<ProtectedRoute element={<HRMetrics />} />} />
        <Route path="/app/hr-metrics/calculator/:metricId" element={<ProtectedRoute element={<HRMetricsCalculator />} />} />

        {/* HR-модуль */}
        <Route path="/app/hr" element={<ProtectedRoute element={<PermissionRoute permissionKey="canViewHRM"><DashboardHR /></PermissionRoute>} />} />
        <Route path="/app/hr/employees" element={<ProtectedRoute element={<EmployeeList />} />} />
        <Route path="/app/hr/employees/new" element={<ProtectedRoute element={<EmployeeForm />} />} />
        <Route path="/app/hr/employees/:id" element={<ProtectedRoute element={<EmployeeCard />} />} />
        <Route path="/app/hr/employees/:id/edit" element={<ProtectedRoute element={<EmployeeForm />} />} />
        <Route path="/app/hr/requisitions" element={<ProtectedRoute element={<RequisitionList />} />} />
        <Route path="/app/hr/requisition/new" element={<ProtectedRoute element={<RequisitionForm />} />} />
        <Route path="/app/hr/requisition/:id" element={<ProtectedRoute element={<RequisitionForm />} />} />
        <Route path="/app/hr/vacancies" element={<ProtectedRoute element={<VacancyList />} />} />
        <Route path="/app/hr/vacancy/:id/kanban" element={<ProtectedRoute element={<CandidateKanban />} />} />
        <Route path="/app/hr/vacancy/:id" element={<ProtectedRoute element={<VacancyDetail />} />} />
        <Route path="/app/hr/vacancy/new" element={<ProtectedRoute element={<VacancyForm />} />} />
        <Route path="/app/hr/vacancy/:id/edit" element={<ProtectedRoute element={<VacancyForm />} />} />
        <Route path="/app/hr/candidate/:id" element={<ProtectedRoute element={<CandidateProfile />} />} />
        <Route path="/app/hr/candidates" element={<ProtectedRoute element={<CandidateList />} />} />
        <Route path="/app/hr/candidates/new" element={<ProtectedRoute element={<CandidateForm />} />} />
        <Route path="/app/hr/candidates/:id" element={<ProtectedRoute element={<CandidateProfile />} />} />
        <Route path="/app/hr/candidates/:id/edit" element={<ProtectedRoute element={<CandidateForm />} />} />

        {/* ★ Массовый подбор (Спринты 2-5) */}
        <Route path="/app/hr/checklist-templates" element={<ProtectedRoute element={<ChecklistTemplatesPage />} />} />
        <Route path="/app/hr/checklist-analytics" element={<ProtectedRoute element={<ChecklistAnalyticsPage />} />} />
        <Route path="/app/hr/mass-funnel" element={<ProtectedRoute element={<MassFunnelPage />} />} />
        <Route path="/app/hr/mass-mailings" element={<ProtectedRoute element={<MassMailingsPage />} />} />
        <Route path="/app/hr/onboarding" element={<ProtectedRoute element={<OnboardingDashboard />} />} />
        <Route path="/app/hr/recruiters-kpi" element={<ProtectedRoute element={<RecruitersKpiPage />} />} />
        <Route path="/app/hr/source-analytics" element={<ProtectedRoute element={<SourceAnalyticsPage />} />} />
        <Route path="/app/hr/companies" element={<ProtectedRoute element={<CompanyList />} />} />
        <Route path="/app/hr/companies/new" element={<ProtectedRoute element={<CompanyForm />} />} />
        <Route path="/app/hr/companies/:id" element={<ProtectedRoute element={<CompanyDetail />} />} />
        <Route path="/app/hr/companies/:id/edit" element={<ProtectedRoute element={<CompanyForm />} />} />
        <Route path="/app/hr/talent-pool" element={<ProtectedRoute element={<TalentPool />} />} />
        <Route path="/app/hr/talent-pool/new" element={<ProtectedRoute element={<TalentPoolForm />} />} />
        <Route path="/app/hr/talent-pool/:id" element={<ProtectedRoute element={<TalentPoolDetail />} />} />
        <Route path="/app/hr/talent-pool/:id/edit" element={<ProtectedRoute element={<TalentPoolForm />} />} />
        <Route path="/app/hr/calendar" element={<ProtectedRoute element={<VacationCalendar />} />} />
        <Route path="/app/hr/documents" element={<ProtectedRoute element={<DocumentsManager />} />} />
        <Route path="/app/hr/email" element={<ProtectedRoute element={<Email />} />} />
        <Route path="/app/hr/kanban" element={<ProtectedRoute element={<PermissionRoute permissionKey="canViewHRM"><KanbanDashboard /></PermissionRoute>} />} />
        <Route
          path="/app/hr-docs"
          element={
            <ProtectedRoute element={
              <PermissionRoute permissionKey="canViewHRDocuments">
                <HRDocumentsLayout />
              </PermissionRoute>
            } />
          }
        >
          <Route index element={<KDPDashboard />} />
          <Route path="documents" element={<HRDocumentsPage key="kdp-documents" defaultTab="documents" hideTabs />} />
          <Route path="templates" element={<HRDocumentsPage key="kdp-templates" defaultTab="templates" hideTabs />} />
          <Route path="onboarding" element={<HRDocumentsPage key="kdp-onboarding" defaultTab="onboarding" hideTabs />} />
          <Route path="orders" element={<KDPOrdersPage />} />
          <Route path="reports" element={<KDPReportsPage />} />
          <Route path="calendar" element={<KDPCalendarPage />} />
          <Route path="certificates" element={<HRCertificatesPage />} />
          <Route path="acquaintances" element={<HRAcquaintancesPage />} />
          <Route path="requests" element={<HRRequestsPage />} />
          <Route path="bulk-sign" element={<HRBulkSignPage />} />
          <Route path="my-hr" element={<MyHrDashboard />} />
          <Route path="salaries" element={<HRSalariesPage />} />
          <Route path="employees" element={<KDPEmployeesPage />} />
          <Route path="schedule" element={<KDPSchedulePage />} />
          <Route path="vacations" element={<KDPVacationsPage />} />
          <Route path="processes" element={<HRProcessesPage />} />
          <Route path="labor-safety" element={<HRLaborSafetyPage />} />
          <Route path="employment-books" element={<HREmploymentBooksPage />} />
          <Route path="settings" element={<KDPSettingsPage />} />
        </Route>

        {/* СРМ Продажи — с Layout */}
        <Route
          path="/app/sales"
          element={
            <ProtectedRoute element={
              <PermissionRoute permissionKey="canViewSalesCRM">
                <SalesLayout />
              </PermissionRoute>
            } />
          }
        >
          <Route index element={<SalesDashboard />} />
          <Route path="deals" element={<SalesDealsPage />} />
          <Route path="tasks" element={<SalesTasksPage />} />
          <Route path="approvals" element={<SalesApprovalsPage />} />
          <Route path="gantt" element={<SalesGanttPage />} />
          <Route path="processes" element={<SalesProcessesPage />} />
          <Route path="boards" element={<SalesBoardsPage />} />
          <Route path="clients" element={<SalesClientsPage />} />
          <Route path="reports" element={<SalesReportsPage />} />
          <Route path="automation" element={<SalesAutomationPage />} />
          <Route path="lead-forms" element={<SalesLeadFormsPage />} />
          <Route path="email-settings" element={<SalesEmailSettingsPage />} />
          <Route path="email-templates" element={<SalesEmailTemplatesPage />} />
          <Route path="email-sequences" element={<SalesEmailSequencesPage />} />
          <Route path="gamification" element={<SalesGamificationPage />} />
          <Route path="settings" element={<SalesSettingsPage />} />
        </Route>

        {/* СРМ Бухгалтерия — с Layout */}
        <Route
          path="/app/accounting"
          element={
            <ProtectedRoute element={
              <PermissionRoute permissionKey="canViewAccountingCRM">
                <AccountingLayout />
              </PermissionRoute>
            } />
          }
        >
          <Route index element={<AccountingDashboard />} />
          <Route path="requests" element={<AccountingRequestsPage />} />
          <Route path="invoices" element={<InvoicesPage />} />
          <Route path="payments" element={<PaymentsPage />} />
          <Route path="calendar" element={<PaymentCalendarPage />} />
          <Route path="counterparties" element={<CounterpartiesPage />} />
          <Route path="operations" element={<OperationsPage />} />
          <Route path="accounts" element={<AccountsPage />} />
          <Route path="reports" element={<AccountingReportsPage />} />
          <Route path="settings" element={<AccountingSettingsPage />} />
          <Route path="bank" element={<BankPage />} />
          <Route path="documents" element={<AccountingDocumentsPage />} />
          <Route path="tax" element={<AccountingTaxPage />} />
        </Route>

        {/* LEARNING_PATHS_ADDED: Layout Route pattern (с Outlet) */}
        <Route
          path="/app/elearning"
          element={
            <ProtectedRoute
              element={
                <PermissionRoute permissionKey="canViewElearning">
                  <ErrorBoundary>
                    <ELearningLayout />
                  </ErrorBoundary>
                </PermissionRoute>
              }
            />
          }
        >
          <Route index element={<ELearningDashboard />} />
          <Route path="tests" element={<TestsList />} />
          <Route path="tests/:testId" element={<TestRunner />} />
          <Route path="tests/:testId/result" element={<TestResultPage />} />
          <Route path="favorites" element={<FavoritesTests />} />
          <Route path="constructor" element={<TestConstructor />} />
          <Route path="constructor/:id" element={<TestConstructor />} />
          <Route path="trash" element={<TrashTests />} />
          <Route path="rating" element={<RatingPage />} />
          <Route path="analytics" element={<AnalyticsPage />} />
          <Route path="benefits" element={<BenefitsList />} />
          <Route path="benefits/new" element={<BenefitConstructor />} />
          <Route path="benefits/:id/edit" element={<BenefitConstructor />} />
          <Route path="task-benefits" element={<TaskBenefitsList />} />
          <Route path="task-benefits/new" element={<TaskBenefitConstructor />} />
          <Route path="task-benefits/:id" element={<TaskBenefitDetail />} />
          <Route path="task-benefits/:id/edit" element={<TaskBenefitConstructor />} />
          <Route path="profile" element={<UserProfile />} />
          <Route path="alerts" element={<AlertsPage />} />
          {/* ★ НОВЫЕ ELearning-роуты */}
          <Route path="my-assignments" element={<MyAssignmentsPage />} />
          <Route path="hr-dashboard" element={<HRDashboardPage />} />
          <Route path="achievements" element={<AchievementsPage />} />
          <Route path="calendar" element={<CalendarPage />} />
          <Route path="comparative" element={<ComparativeAnalyticsPage />} />
          <Route path="kpi" element={<KPIPage />} />
          <Route path="comments" element={<CommentsPage />} />
          <Route path="audit" element={<AuditLogPage />} />
          <Route path="auto-rules" element={<ELearningAutoRulesPage />} />
          <Route path="paths" element={<LearningPathsPage />} />
          <Route path="paths/new" element={<LearningPathConstructor />} />
          <Route path="paths/:id/edit" element={<LearningPathConstructor />} />
          <Route path="paths/:id" element={<LearningPathDetailPage />} />
        </Route>

        {/* ★ Публичная верификация сертификата */}
        <Route path="/certificate/verify/:number?" element={<VerifyCertificatePage />} />
        <Route path="/certificate/verify" element={<VerifyCertificatePage />} />
        {/* ★ Печать сертификата A4 */}
        <Route path="/certificate/print/:number" element={<CertificatePrintPage />} />

        <Route path="/admin" element={<AdminRoute element={<AdminPage />} />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />

        

        <Route path="/app/competency-interview" element={<ProtectedRoute element={<CompetencyInterview />} />} />
        <Route path="/app/projective-interview" element={<ProtectedRoute element={<ProjectiveInterview />} />} />
        <Route path="/app/case-interview" element={<ProtectedRoute element={<CaseInterview />} />} />
        <Route path="/app/three-plus-minus-interview" element={<ProtectedRoute element={<ThreePlusMinusInterview />} />} />
        <Route path="/app/provocation-interview" element={<ProtectedRoute element={<ProvocationInterview />} />} />
        <Route path="/app/information-credibility" element={<ProtectedRoute element={<InformationCredibility />} />} />
        <Route path="/app/kpi" element={<ProtectedRoute element={<KPIProfiles />} />} />
        <Route path="/elearning-test/:token" element={<PublicTestRunner />} />
      
        {/* Восстановленные модули */}
        <Route path="/app/interview-assistant" element={<ProtectedRoute element={<InterviewAssistant />} />} />
        <Route path="/app/recruitment-analytics" element={<ProtectedRoute element={<PermissionRoute permissionKey="canViewDataDriven"><RecruitmentLayout /></PermissionRoute>} />}>
          <Route index element={<RecruitmentDashboard />} />
          <Route path="vacancies" element={<Vacancies />} />
          <Route path="vacancies/:id" element={<RecruitmentVacancyDetail />} />
          <Route path="candidates" element={<Candidates />} />
          <Route path="candidates/:id" element={<RecruitmentCandidateDetail />} />
          <Route path="requisitions" element={<Requisitions />} />
          <Route path="requisitions/:id" element={<RecruitmentRequisitionDetail />} />
          <Route path="vacancy-aging" element={<VacancyAging />} />
          <Route path="rejection-analysis" element={<RejectionAnalysis />} />
          <Route path="plan-vs-actual" element={<PlanVsActual />} />
          <Route path="predictive" element={<PredictiveDashboard />} />
          <Route path="recruiter-performance" element={<RecruiterPerformanceDashboard />} />
          <Route path="performance" element={<RecruiterPerformanceDashboard />} />
          <Route path="performance/:recruiterId" element={<RecruiterDetail />} />
          <Route path="recruiter/:recruiterId" element={<RecruiterDetail />} />
          <Route path="daily-digest" element={<DailyDigest />} />
          <Route path="labor-market" element={<LaborMarketDashboard />} />
          <Route path="alerts" element={<AlertsPage />} />
        </Route>
        <Route path="/app/psychodiagnostics" element={<ProtectedRoute element={<PsychodiagnosticsPage />} />} />
        <Route path="/app/psychodiagnostics/:testId" element={<ProtectedRoute element={<PsychodiagnosticsTestRunner />} />} />
        <Route path="/app/templates" element={<ProtectedRoute element={<TemplatesPage />} />} />

        <Route path="/app/calendar" element={<ProtectedRoute element={<UnifiedCalendar />} />} />
        <Route path="/app/analytics/roles" element={<ProtectedRoute element={<RoleAnalytics />} />} />

        {/* Catch-all — ПОСЛЕДНИМ */}
        <Route path="*" element={<NotFoundPage />} />
        </Routes>
    </Suspense>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <ConfirmProvider>
          <ToastProvider>
            <AppRoutes />
        <PlacementModalHost />
          </ToastProvider>
        </ConfirmProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
